import { DatePreset, FilterState, AuditLog, PaginationState } from "./types";
import { User } from "../../../features/user/userSlice";

export const formatTimestamp = (
  timestamp: string
): { fullDate: string; relativeTime: string } => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();

  const fullDate = date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  let relativeTime = "";
  if (diffInMs < 60000) {
    // less than 1 minute
    relativeTime = "Just now";
  } else if (diffInMs < 3600000) {
    // less than 1 hour
    const minutes = Math.floor(diffInMs / 60000);
    relativeTime = `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  } else if (diffInMs < 86400000) {
    // less than 1 day
    const hours = Math.floor(diffInMs / 3600000);
    relativeTime = `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (diffInMs < 2592000000) {
    // less than 30 days
    const days = Math.floor(diffInMs / 86400000);
    relativeTime = `${days} day${days > 1 ? "s" : ""} ago`;
  } else if (diffInMs < 31536000000) {
    // less than 1 year
    const months = Math.floor(diffInMs / 2592000000);
    relativeTime = `${months} month${months > 1 ? "s" : ""} ago`;
  } else {
    const years = Math.floor(diffInMs / 31536000000);
    relativeTime = `${years} year${years > 1 ? "s" : ""} ago`;
  }

  return { fullDate, relativeTime };
};

export const getDateRangeFromPreset = (
  preset: DatePreset
): { start: Date; end: Date } => {
  const start = new Date();
  const end = new Date();

  switch (preset) {
    case "today":
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case "thisWeek":
      const dayOfWeek = start.getDay();
      const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case "thisMonth":
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case "lastMonth":
      start.setMonth(start.getMonth() - 1);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(end.getMonth() - 1);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
      break;
    case "lastYear":
      start.setFullYear(start.getFullYear() - 1);
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      end.setFullYear(end.getFullYear() - 1);
      end.setMonth(11, 31);
      end.setHours(23, 59, 59, 999);
      break;
    default:
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
  }

  return { start, end };
};

export const filterLogs = (
  logs: AuditLog[],
  filters: FilterState,
  userInfoMap: Record<string, User>
): AuditLog[] => {
  return logs.filter((log) => {
    // User filter - check if the user's email matches the filter
    if (filters.user) {
      const userInfo = userInfoMap[log.user_id];
      if (!userInfo || userInfo.email !== filters.user) {
        return false;
      }
    }

    // Resource type filter
    if (filters.resourceType && log.resource_type !== filters.resourceType) {
      return false;
    }

    // Action filter
    if (filters.action && log.action !== filters.action) {
      return false;
    }

    // Date filter
    if (filters.dateRange.start && filters.dateRange.end) {
      const logDate = new Date(log.timestamp);
      if (logDate < filters.dateRange.start || logDate > filters.dateRange.end) {
        return false;
      }
    }

    return true;
  });
};

export const paginateLogs = (logs: AuditLog[], page: number, itemsPerPage: number) => {
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return logs.slice(startIndex, endIndex);
};

export const initialFilters: FilterState = {
  user: "",
  resourceType: "",
  action: "",
  dateRange: {
    start: null,
    end: null,
    preset: "",
  },
};

export const initialPaginationState: PaginationState = {
  currentPage: 1,
  itemsPerPage: 10,
  totalItems: 0,
};
